import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';

import {
  canActivate, canCredit, canSuspend, canTerminate,
  type ResellerDetail, type ResellerSession, type ResellerTransaction,
} from '@domain/reseller/Reseller';
import {
  InvalidResellerOp,
  activateReseller,
  creditReseller,
  suspendReseller,
  terminateReseller,
} from '@application/reseller/manageReseller';
import { apiClient } from '@infrastructure/api/client';
import { HttpResellerRepository } from '@infrastructure/api/HttpResellerRepository';
import { DataTable, type DataTableColumn } from '@app/components/data-table/DataTable';
import { Modal } from '@app/components/Modal';
import { TabbedListSection } from '@app/components/TabbedListSection';
import { formatDateTime } from '@app/lib/format';
import { StatusBadge } from '@app/components/StatusBadge';

const repo = new HttpResellerRepository(apiClient);

/** The API's own message ({error} or {detail}) rather than "Request failed with status code 400". */
function errorText(e: unknown): string {
  if (e instanceof InvalidResellerOp) return e.message;
  if (axios.isAxiosError(e)) {
    const body = e.response?.data as { error?: string; detail?: string } | undefined;
    if (body?.error || body?.detail) return String(body.error ?? body.detail);
  }
  return (e as Error).message;
}

export function ResellerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<ResellerDetail | null>(null);
  const [error, setError] = useState<{ kind: 'not_found' | 'other'; message: string } | null>(null);
  const [showCredit, setShowCredit] = useState(false);

  const refresh = () => {
    if (!id) return;
    repo.get(id).then((d) => { setData(d); setError(null); }).catch((e) => {
      if (axios.isAxiosError(e) && e.response?.status === 404) {
        setError({ kind: 'not_found', message: 'Reseller not found.' });
      } else {
        setError({ kind: 'other', message: (e as Error).message });
      }
    });
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

  if (error) {
    return (
      <div className="rounded-lg border bg-white p-6 text-center shadow-sm">
        <p className="text-base text-slate-700">{error.message}</p>
        <p className="mt-3">
          <Link to="/resellers" className="text-sm text-blue-600 hover:underline">← Back to Resellers</Link>
        </p>
        {error.kind !== 'not_found' && (
          <p className="mt-3 text-xs text-slate-500">{error.message}</p>
        )}
      </div>
    );
  }
  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;

  const handleActivate = async () => {
    try { await activateReseller(repo)(data); refresh(); }
    catch (e) { alert(errorText(e)); }
  };
  const handleSuspend = async () => {
    const reason = window.prompt('Suspend reason (logged):');
    if (!reason) return;
    try { await suspendReseller(repo)(data, reason); refresh(); }
    catch (e) { alert(errorText(e)); }
  };
  const handleTerminate = async () => {
    const reason = window.prompt('PERMANENT — terminate reason (logged):');
    if (!reason) return;
    if (!window.confirm(`Permanently terminate "${data.name}"? This cannot be undone.`)) return;
    try { await terminateReseller(repo)(data, reason); refresh(); }
    catch (e) { alert(errorText(e)); }
  };
  const handleRotate = async () => {
    if (!window.confirm(`Rotate SMPP password for "${data.system_id}"? Every live session is disconnected now; the reseller must bind again with the new password.`)) return;
    try {
      // The API returns new_password; this read r.password and showed "(not returned)"
      const r = await repo.rotatePassword(data.id);
      window.prompt(
        `New SMPP password (copy now — not shown again). ${r.sessions_disconnected} session(s) disconnected.`,
        r.new_password,
      );
      refresh();
    } catch (e) { alert(errorText(e)); }
  };

  return (
    <section>
      <div className="mb-3"><Link to="/resellers" className="text-sm text-blue-600 hover:underline">← Resellers</Link></div>
      <div className="mb-4 flex flex-col items-start gap-3 sm:flex-row sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold">{data.name}</h2>
          <p className="text-sm text-slate-600">
            <code className="font-mono text-xs">{data.system_id}</code>{' '}
            <StatusBadge value={data.status} />
            {data.suspension_reason && <span className="ml-2 text-xs text-slate-500">— {data.suspension_reason}</span>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 text-sm">
          {canActivate(data) && (
            <button type="button" onClick={handleActivate} className="rounded border px-3 py-1 text-green-700">
              {data.status === 'pending' ? 'Activate' : 'Reactivate'}
            </button>
          )}
          {canSuspend(data) && (
            <button type="button" onClick={handleSuspend} className="rounded border px-3 py-1 text-red-700">Suspend</button>
          )}
          {canCredit(data) && (
            <button type="button" onClick={() => setShowCredit(true)} className="rounded bg-blue-600 px-3 py-1 text-white">Add credit</button>
          )}
          {data.status !== 'terminated' && (
            <button type="button" onClick={handleRotate} className="rounded border px-3 py-1">Rotate password</button>
          )}
          {canTerminate(data) && (
            <button type="button" onClick={handleTerminate} className="rounded border border-red-300 px-3 py-1 text-red-700">Terminate</button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <KCard title="Balance" value={`$${data.balance}`} sub={`Limit $${data.credit_limit} · low ≤ $${data.low_balance_threshold}`} />
        <KCard title="Tier" value={String(data.tier_level)} sub={`$${data.price_per_segment}/seg · ${data.commission_percent}% commission`} />
        <KCard title="Live sessions" value={data.active_sessions_count} sub={`max ${data.max_connections} · ${data.rate_limit_per_second}/s`} />
        <KCard title="Today" value={data.today_message_count} sub="messages" />
        <KCard title="Quotas" value={`${data.daily_quota ?? '∞'} / day`} sub={`${data.monthly_quota ?? '∞'} / month`} />
        <KCard title="Auto-recharge" value={data.auto_recharge ? 'enabled' : 'disabled'} sub={data.auto_recharge ? `+$${data.auto_recharge_amount} when ≤ $${data.auto_recharge_threshold}` : ''} />
        <KCard title="TLS" value={data.require_tls ? 'required' : 'optional'} />
        <KCard title="IP allowlist" value={(data.allowed_ips ?? []).length === 0 ? 'any' : `${data.allowed_ips.length} ${data.allowed_ips.length === 1 ? 'entry' : 'entries'}`} sub={(data.allowed_ips ?? []).join(', ').slice(0, 60)} />
        <KCard title="Contact" value={data.contact_email || '—'} sub={data.contact_phone || ''} />
      </div>

      <div className="mt-6">
        <TabbedListSection
          title="Activity"
          tabs={[
            { id: 'sessions', label: 'SMPP sessions', render: () => <SessionsTab resellerId={data.id} /> },
            { id: 'transactions', label: 'Transactions', render: () => <TransactionsTab resellerId={data.id} /> },
          ]}
        />
      </div>

      <CreditModal
        open={showCredit}
        reseller={data}
        onClose={() => setShowCredit(false)}
        onSaved={(updated) => setData(updated)}
      />
    </section>
  );
}

function KCard({ title, value, sub }: { title: string; value: string | number; sub?: string }) {
  return (
    <div className="rounded-lg border bg-white p-4 shadow-sm">
      <div className="text-xs uppercase text-slate-500">{title}</div>
      <div className="mt-1 break-all text-base font-medium text-slate-900">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

function SessionsTab({ resellerId }: { resellerId: string }) {
  const cols: DataTableColumn<ResellerSession>[] = [
    { id: 'connected_at', header: 'Connected', accessor: (r) => formatDateTime(r.connected_at) },
    { id: 'client_ip', header: 'Client IP', accessor: (r) => <code className="font-mono text-xs">{r.client_ip}</code> },
    { id: 'is_tls', header: 'TLS', accessor: (r) => (r.is_tls ? 'yes' : 'no') },
    { id: 'status', header: 'Status', accessor: (r) => <StatusBadge value={r.status} /> },
    { id: 'messages_sent', header: 'Sent', accessor: 'messages_sent' },
    { id: 'disconnected_at', header: 'Disconnected', accessor: (r) => r.disconnected_at ? formatDateTime(r.disconnected_at) : '—' },
  ];
  return (
    <DataTable<ResellerSession>
      columns={cols}
      fetcher={(p) => repo.listSessions(resellerId, p)}
      pagination="offset"
      defaultPageSize={25}
      rowKey={(r) => r.id}
      emptyState={<span data-testid="datatable-empty">No SMPP binds recorded.</span>}
    />
  );
}

function TransactionsTab({ resellerId }: { resellerId: string }) {
  const cols: DataTableColumn<ResellerTransaction>[] = [
    { id: 'created_at', header: 'When', accessor: (r) => formatDateTime(r.created_at) },
    { id: 'transaction_type', header: 'Type', accessor: 'transaction_type' },
    { id: 'amount', header: 'Amount', accessor: (r) => `$${r.amount}` },
    { id: 'balance_after', header: 'Balance after', accessor: (r) => `$${r.balance_after}` },
    { id: 'reason', header: 'Description', accessor: 'reason' },
  ];
  return (
    <DataTable<ResellerTransaction>
      columns={cols}
      fetcher={(p) => repo.listTransactions(resellerId, p)}
      pagination="offset"
      defaultPageSize={25}
      rowKey={(r) => r.id}
      emptyState={<span data-testid="datatable-empty">No transactions yet.</span>}
    />
  );
}

function CreditModal({
  open, reseller, onClose, onSaved,
}: {
  open: boolean;
  reseller: ResellerDetail;
  onClose: () => void;
  onSaved: (r: ResellerDetail) => void;
}) {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  return (
    <Modal open={open} title={`Add credit — ${reseller.name}`} onClose={onClose}>
      <form
        onSubmit={async (e) => {
          e.preventDefault(); setError(null); setSubmitting(true);
          try {
            const updated = await creditReseller(repo)(reseller, amount, description);
            onSaved(updated); onClose();
            setAmount(''); setDescription('');
          } catch (err) {
            setError(errorText(err));
          } finally { setSubmitting(false); }
        }}
        className="space-y-3 text-sm"
      >
        <div className="rounded bg-slate-50 px-3 py-2 text-xs text-slate-600">
          Current balance: <strong>${reseller.balance}</strong>
        </div>
        <label className="block">
          Amount ($)
          <input
            type="number" step="0.01" min="0.01" required
            value={amount} onChange={(e) => setAmount(e.target.value)}
            className="mt-1 w-full rounded border px-2 py-1"
          />
        </label>
        <label className="block">
          Description (logged in audit trail)
          <textarea
            required minLength={3} rows={3}
            value={description} onChange={(e) => setDescription(e.target.value)}
            className="mt-1 w-full rounded border px-2 py-1"
          />
        </label>
        {error && <p className="text-red-700">{error}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="rounded border px-3 py-1">Cancel</button>
          <button type="submit" disabled={submitting} className="rounded bg-blue-600 px-3 py-1 text-white disabled:opacity-50">
            {submitting ? 'Applying…' : 'Add credit'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
